import { helpers, models } from '@kurtosys/ksys-app-template';
import { computed, observable } from 'mobx';
import stringify from 'json-stable-stringify';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import {
    EventTypeCache,
    ListenForOptions,
} from '../models/configuration/ListenForOptions';

type EventBusStoreCallback = (customEvent: CustomEvent<any>) => Promise<void>;
type EventBusStoreCallbacks = Partial<
    Record<models.eventBus.EventType, EventBusStoreCallback>
>;
interface ListenOptions<TDetail extends models.eventBus.IBaseEventDetail> {
    callback: EventBusStoreCallback;
    updateRequiredTracking?: boolean;
    runOnce?: boolean;
}

export class EventBusStore extends BaseStore {
    callbacks: EventBusStoreCallbacks | undefined;
    eventCache: Map<string, string> = new Map();
    helper: helpers.EventBusHelper | undefined;
    @observable.ref requiredCallbackTracking: Record<string, boolean> = {};

    constructor(stores: Stores) {
        super(stores);
    }

    @computed
    get listenFor(): Array<ListenForOptions> {
        if (this.stores.data) {
            return this.stores.data.config.listenFor;
        }

        return [];
    }

    @computed
    get applicationId(): string | undefined {
        if (this.stores.data) {
            return this.stores.data.config.applicationId;
        }

        return;
    }

    @computed
    public get requiredCallbacksCompleted() {
        if (Object.keys(this.requiredCallbackTracking).length === 0) {
            return true;
        }

        let completed = true;
        for (const name in this.requiredCallbackTracking) {
            if (!this.requiredCallbackTracking[name]) {
                completed = false;
                break;
            }
        }
        return completed;
    }

    setupHelper() {
        this.helper = new helpers.EventBusHelper(this.applicationId);
    }

    async initialize(callbacks: EventBusStoreCallbacks | undefined) {
        this.callbacks = {
            [models.eventBus.EventType.globalInputsChange]: this.stores
                .inputsStore.handleUpdate,
            ...callbacks,
        };
    }

    isListeningFor(appId: string, eventName: string): boolean {
        const app = this.listenFor.find(appConfig => {
            return appConfig.appId === appId;
        });
        const eventConfig =
            app &&
            // tslint:disable-next-line:no-shadowed-variable
            app.eventTypes.find(eventConfig => {
                return eventConfig.name === eventName;
            });

        return Boolean(eventConfig);
    }

    /**
     * Start listening for events that are required for initial load
     */
    public async register() {
        const listenForEvents = this.listenFor;
        if (!this.helper) {
            this.setupHelper();
        }

        if (listenForEvents.length > 0 && this.helper !== undefined) {
            listenForEvents.forEach(listenForEvent => {
                const appId = listenForEvent.appId;
                const appEventTypes = listenForEvent.eventTypes;

                if (appEventTypes.length > 0) {
                    appEventTypes.forEach(appEvent => {
                        const name = appEvent.name as models.eventBus.EventType;
                        const requiredForInitialLoad =
                            appEvent.requiredForInitialLoad;
                        const runOnce = appEvent.runOnce;
                        const cache = appEvent.cache;
                        // Wait for the eventBus Store to retrieve all events that are required before continuing with initialzing of the app
                        const targetCallback =
                            this.callbacks && this.callbacks[name];
                        if (targetCallback) {
                            const storageValue = this.getStorageValue(
                                appId,
                                name,
                                cache
                            );

                            if (
                                storageValue &&
                                this.hasEventDetailChanged(
                                    storageValue.detail,
                                    name
                                )
                            ) {
                                targetCallback(storageValue);
                            }

                            // Checks if the event is required and if we already have the event, then we don't need to set up additional tracking for it
                            let updateRequiredTracking = false;
                            if (!storageValue && requiredForInitialLoad) {
                                updateRequiredTracking = true;
                                this.requiredCallbackTracking = {
                                    ...this.requiredCallbackTracking,
                                    [`${appId}-${name}`]: false,
                                };
                            }

                            // add an event listener if the runOnce condition hasn't already been met.
                            if (!runOnce || (runOnce && !storageValue)) {
                                this.listen(appId, name, {
                                    runOnce,
                                    updateRequiredTracking,
                                    callback: targetCallback,
                                });
                            }
                        }
                    });
                }
            });
        }
    }

    validateEventCache(
        eventValue: Record<string, any>,
        appId: string,
        eventType: string
    ) {
        const key = eventType;
        const evtValue = stringify(eventValue);
        if (
            !this.eventCache.has(key) ||
            this.eventCache.get(key) !== evtValue
        ) {
            this.eventCache.set(key, evtValue);
            return true;
        }
        return false;
    }

    getStorageValue(
        appId: string,
        eventType: models.eventBus.EventType,
        cache?: EventTypeCache
    ) {
        if (cache && cache.enabled === false) {
            return;
        }
        const eventDetails = sessionStorage.getItem('eventDetails');
        if (eventDetails) {
            try {
                const parsedDetails = JSON.parse(eventDetails);
                if (
                    parsedDetails &&
                    parsedDetails[appId] &&
                    parsedDetails[appId][eventType]
                ) {
                    return parsedDetails[appId][eventType];
                }
            } catch (e) {
                console.warn(`Failed to parse event storage for ${eventType}`);
            }
        }
        return;
    }

    trigger(
        type: models.eventBus.EventType,
        detail: { detail: Record<string, any> }
    ) {
        if (this.helper) {
            this.helper.trigger(type, detail);
        } else {
            console.warn(
                `Unable to trigger event, as no the helper not yet defined. The helper is defined as part of the event bus store register.`
            );
        }
    }

    /**
     * Retrieves the application id from event detail's application source or the legacy identifier field
     *
     * @private
     * @template T
     * @param event
     * @returns
     */
    public getSourceAppIdFromEvent<T extends models.eventBus.IBaseEventDetail>(
        event: CustomEvent<T>
    ): string {
        const { detail } = event;
        let id = '';
        const { sourceApplication } = detail;
        if (sourceApplication && sourceApplication.applicationId) {
            id = sourceApplication.applicationId;
        } else if (sourceApplication && sourceApplication.templateId) {
            id = sourceApplication.templateId;
        }
        // Fallback to legacy
        if (id === '') {
            const legacyDetail = detail as Record<string, any>;
            if (legacyDetail.identifier) {
                id = legacyDetail.identifier;
            }
        }
        return id;
    }

    private hasEventDetailChanged(
        eventValue: Record<string, any>,
        eventType: string
    ) {
        const evtValue = stringify(eventValue);
        if (
            !this.eventCache.has(eventType) ||
            this.eventCache.get(eventType) !== evtValue
        ) {
            this.eventCache.set(eventType, evtValue);
            return true;
        }
        return false;
    }

    private listen<T extends models.eventBus.IBaseEventDetail>(
        targetAppId: string,
        type: models.eventBus.EventType,
        options: ListenOptions<T>
    ) {
        const helper = this.helper;
        if (helper) {
            const {
                callback,
                updateRequiredTracking: updatedRequiredTracking = false,
                runOnce = false,
            } = options;
            const eventHandler = (evt: Event) => {
                const customEvent = evt as CustomEvent<T>;

                /**
                 * Due to this being the listener, if a legacy app that has not pulled upstream or has not been upgraded to v4,
                 * when the legacy app triggers an event that this v4 app catches, we will need to transform the app initialised detail
                 * into the sourceApplication detail we expect.
                 *
                 * Note, that the computed source application detail will not contain the appropriate application id, as it didn't exist.
                 *
                 * TODO: Once ALL apps have been upgraded to v4, this code will fall away
                 */
                if (type === 'ksys-app-initialized') {
                    if (!customEvent.detail.sourceApplication) {
                        const eventDetail = customEvent.detail as Record<
                            string,
                            any
                        >;
                        customEvent.detail.sourceApplication = {
                            applicationCode: eventDetail.appGuid || '',
                            templateId: eventDetail.templateId || '',
                            applicationId: eventDetail.templateId || '',
                            configurationKey:
                                eventDetail.configKey || 'default',
                            styleKey: eventDetail.styleKey || 'default',
                            themeKey: eventDetail.themeKey || 'default',
                        };
                    }
                }

                const eventSourceAppId = this.getSourceAppIdFromEvent(
                    customEvent
                );
                if (eventSourceAppId === targetAppId) {
                    if (runOnce) {
                        helper.unregister(type, eventHandler);
                    }
                    if (updatedRequiredTracking) {
                        this.requiredCallbackTracking = {
                            ...this.requiredCallbackTracking,
                            [`${targetAppId}-${type}`]: true,
                        };
                    }
                    if (this.hasEventDetailChanged(customEvent.detail, type)) {
                        callback(customEvent);
                    }
                }
            };
            this.helper.register(type, eventHandler);
        } else {
            console.warn(
                `Unable to register event type "${type}", as the Event Bus Helper is not yet defined. It is defined as part of the event bus store register method.`
            );
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/EventBusStore.ts